'use client';
import { useState } from 'react';

import { useAtomValue } from 'jotai';

import { Loading } from '@core/Components/Loading/Loading';
import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import {
  getFRInterlinkedLocation,
  getUKInterlinkedLocation,
} from '@core/Utils/Location/GetInterlinkedLocation';
import { Typography } from '@gds/Typography/Typography';
import { regionAtom } from '@search/Atoms/RegionAtom';

import { LinkBlock } from 'Components/LinkBlock/LinkBlock';
import type { LocationAttrs } from 'Components/SearchWidget/Search.entity';

import { InterlinkingRegionItem } from '../InterlinkingRegionItem/InterlinkingRegionItem';
import { LocationInterlinkingMobileView } from './LocationInterlinkingMobileView';

import styles from './LocationInterlinking.module.css';

export const LocationInterlinking = ({
  locale,
  strs,
  locationAttrs,
  showOnlyFilteredRegions,
  tenant,
}: {
  locale: Locale;
  strs: Record<string, string>;
  locationAttrs: LocationAttrs;
  showOnlyFilteredRegions?: boolean;
  tenant?: Tenant;
}) => {
  const [shouldShowLoader, setShouldShowLoader] = useState(false);
  const handleClick = () => {
    setShouldShowLoader(true);
  };

  const selectedRegion = useAtomValue(regionAtom);

  const { regions, areas } = locationAttrs;

  if (!regions || !areas) return null;

  if (showOnlyFilteredRegions && selectedRegion.region === 'anyregion') {
    return null;
  }
  const baseUrl = `${!tenant ? `${locale}/` : ''}`;

  const interlinkingItems =
    locale === 'uk'
      ? getUKInterlinkedLocation(locale, regions, tenant)
      : getFRInterlinkedLocation(locale, areas);

  const areasInRegion =
    locale === 'uk'
      ? locationAttrs?.areas.filter(area => area.region === selectedRegion.region)
      : locationAttrs?.areas.filter(area => area.regionCode === selectedRegion.regionCode);

  const areaCTAs = areasInRegion?.map(item => {
    const regionName = locale === 'uk' ? item.region : item.regionCode;
    const areaName = locale === 'uk' ? item.area : item.areaCode;

    return {
      ctaText: item.shortName,
      link: `/${baseUrl}dealerships/${regionName}/${areaName}`,
    };
  });

  const areasHeading = `${strs.areasInHeader} ${selectedRegion.displayName}`;
  return (
    <>
      {shouldShowLoader ? (
        <Loading />
      ) : (
        <>
          {areaCTAs && showOnlyFilteredRegions && (
            <LinkBlock heading={areasHeading} ctas={areaCTAs} />
          )}

          {!showOnlyFilteredRegions &&
            interlinkingItems.map(interlinkingItem => (
              <section className={styles.interlinking} key={interlinkingItem.heading}>
                <div className={styles.title}>
                  <Typography variant="h2">
                    {' '}
                    {strs.areasInHeader
                      ? `${strs.areasInHeader} ${interlinkingItem.heading}`
                      : interlinkingItem.heading}
                  </Typography>
                </div>
                {interlinkingItem.regions && (
                  <>
                    {/* only show this component for tablet and desktop */}
                    <ul
                      className={`${styles.hideSectionForTblt} ${styles.locationLayout}`}
                      onClick={handleClick}
                    >
                      {interlinkingItem.regions.map(region => (
                        <InterlinkingRegionItem key={region.name} region={region} />
                      ))}
                    </ul>
                    {/* only show this component for mobile */}
                    <LocationInterlinkingMobileView
                      interlinkingItem={interlinkingItem}
                      viewMoreCtaText={strs.viewMoreCta}
                    />
                  </>
                )}
              </section>
            ))}
        </>
      )}
    </>
  );
};
